function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _classCallCheck(a, n) { if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function"); }
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import * as mat4 from '../vec/mat4.js';

/**
 * @module ol/webgl/Canvas
 */

var VERTEX_SHADER = "\n  attribute vec4 a_position;\n  attribute vec4 a_texcoord;\n\n  uniform mat4 u_matrix;\n  uniform mat4 u_textureMatrix;\n\n  varying vec2 v_texcoord;\n\n  void main() {\n    gl_Position = u_matrix * a_position;\n    vec2 texcoord = (u_textureMatrix * a_texcoord).xy;\n    v_texcoord = texcoord;\n  }\n";
var FRAGMENT_SHADER = "\n  precision mediump float;\n\n  varying vec2 v_texcoord;\n\n  uniform sampler2D u_texture;\n\n  void main() {\n    if (\n      v_texcoord.x < 0.0 ||\n      v_texcoord.y < 0.0 ||\n      v_texcoord.x > 1.0 ||\n      v_texcoord.y > 1.0\n    ) {\n      discard;\n    }\n    gl_FragColor = texture2D(u_texture, v_texcoord);\n  }\n";

/** @typedef {import("../transform.js").Transform} Matrix */

/**
 * Canvas-like operations implemented in webgl.
 */
export var Canvas = /*#__PURE__*/function () {
  /**
   * @param {WebGLRenderingContext} gl Context to render in.
   */
  function Canvas(gl) {
    _classCallCheck(this, Canvas);
    /**
     * @private
     * @type {WebGLRenderingContext}
     */
    this.gl_ = gl;

    /**
     * @private
     * @type {WebGLProgram}
     */
    this.program_ = createProgram(gl, FRAGMENT_SHADER, VERTEX_SHADER);
    this.positionLocation = gl.getAttribLocation(this.program_, 'a_position');
    this.texcoordLocation = gl.getAttribLocation(this.program_, 'a_texcoord');
    this.matrixLocation = gl.getUniformLocation(this.program_, 'u_matrix');
    this.textureMatrixLocation = gl.getUniformLocation(this.program_, 'u_textureMatrix');
    this.textureLocation = gl.getUniformLocation(this.program_, 'u_texture');
    this.positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    this.positions = [0, 0, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1];
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.positions), gl.STATIC_DRAW);
    this.texcoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texcoordBuffer);
    this.texcoords = [0, 0, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1];
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.texcoords), gl.STATIC_DRAW);
  }

  /**
   * 2dContext drawImage call implemented in webgl.
   * Unlike images, textures do not have a width and height associated
   * with them so we'll pass in the width and height of the texture.
   *
   * @param {WebGLTexture} tex Image to draw.
   * @param {number} texWidth Image width.
   * @param {number} texHeight Image height.
   * @param {number} srcX Top-left x-point to read src image.
   * @param {number} srcY Top-left y-point to read src image.
   * @param {number} [srcWidth] Width of source to read.
   * @param {number} [srcHeight] Height of source to read.
   * @param {number} [dstX] Top-left x-point of destination.
   * @param {number} [dstY] Top-left y-point of destination.
   * @param {number} [dstWidth] Width of written image in destination.
   * @param {number} [dstHeight] Height of written image in destination.
   * @param {number} [width] Width of canvas.
   * @param {number} [height] Height of canvas.
   */
  return _createClass(Canvas, [{
    key: "drawImage",
    value: function drawImage(tex, texWidth, texHeight, srcX, srcY, srcWidth, srcHeight, dstX, dstY, dstWidth, dstHeight, width, height) {
      var gl = this.gl_;
      if (dstX === undefined) {
        dstX = srcX;
      }
      if (dstY === undefined) {
        dstY = srcY;
      }
      if (srcWidth === undefined) {
        srcWidth = texWidth;
      }
      if (srcHeight === undefined) {
        srcHeight = texHeight;
      }
      if (dstWidth === undefined) {
        dstWidth = srcWidth;
      }
      if (dstHeight === undefined) {
        dstHeight = srcHeight;
      }
      if (width === undefined) {
        width = gl.canvas.width;
      }
      if (height === undefined) {
        height = gl.canvas.height;
      }
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.useProgram(this.program_);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
      gl.enableVertexAttribArray(this.positionLocation);
      gl.vertexAttribPointer(this.positionLocation, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.texcoordBuffer);
      gl.enableVertexAttribArray(this.texcoordLocation);
      gl.vertexAttribPointer(this.texcoordLocation, 2, gl.FLOAT, false, 0, 0);

      // matrix for converting pixels to clip space
      var matrix = mat4.orthographic(0, width, 0, height, -1, 1);
      matrix = mat4.translate(matrix, dstX, dstY, 0);
      matrix = mat4.scale(matrix, dstWidth, dstHeight, 1);
      gl.uniformMatrix4fv(this.matrixLocation, false, matrix);
      var texMatrix = mat4.translation(srcX / texWidth, srcY / texHeight, 0);
      texMatrix = mat4.scale(texMatrix, srcWidth / texWidth, srcHeight / texHeight, 1);
      gl.uniformMatrix4fv(this.textureMatrixLocation, false, texMatrix);
      gl.uniform1i(this.textureLocation, 0);
      gl.drawArrays(gl.TRIANGLES, 0, this.positions.length / 2);
    }
  }]);
}();

/**
 * @param {WebGLRenderingContext} gl Rendering Context.
 * @param {GLenum} type Type of shader.
 * @param {string} source source of shader.
 * @return {WebGLShader} [progam] The program.
 */
function createShader(gl, type, source) {
  var shader = gl.createShader(type);
  if (shader === null) {
    throw new Error('Shader compilation failed');
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    var log = gl.getShaderInfoLog(shader);
    if (log === null) {
      throw new Error('Shader info log creation failed');
    }
    throw new Error(log);
  }
  return shader;
}

/**
 * @param {WebGLRenderingContext} gl Rendering Context.
 * @param {string} fragmentSource Fragment shader source.
 * @param {string} vertexSource Vertex shader source.
 * @return {WebGLProgram} [progam] The program.
 */
export function createProgram(gl, fragmentSource, vertexSource) {
  var program = gl.createProgram();
  var vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexSource);
  var fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  if (program === null) {
    throw new Error('Program creation failed');
  }
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    var log = gl.getProgramInfoLog(program);
    if (log === null) {
      throw new Error('Program info log creation failed');
    }
    throw new Error();
  }
  return program;
}